import type { FraudAlertInfo } from '@iris/shared';
import { unwrap } from '@iris/shared';
import { useAuth } from '@iris/ui/auth/auth-provider';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useShops } from '../shop-context';

/** The badge: open alerts of the selected shop, refreshed every 5 minutes (owner decision M25: no staff push). */
export function useOpenAlerts() {
  const { api } = useAuth();
  const { current } = useShops();
  const enabled = Boolean(current && current.role !== 'CASHIER' && current.status === 'ACTIVE');
  const query = useQuery({
    queryKey: ['alerts-open', current?.merchantId],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/merchant/alerts', { params: { query: { status: 'open' } } })),
    enabled,
    refetchInterval: 300_000,
  });
  return enabled ? (query.data?.open ?? 0) : 0;
}

export function useAlerts(status: 'open' | 'all') {
  const { api } = useAuth();
  const { current } = useShops();
  return useInfiniteQuery({
    queryKey: ['alerts', current?.merchantId, status],
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET('/api/v1/merchant/alerts', {
          params: { query: { status, ...(pageParam ? { cursor: pageParam } : {}) } },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
    enabled: Boolean(current),
  });
}

/** One sentence with who and the numbers (T0 §9.2: every alert lists its evidence). */
export function useAlertText() {
  const { t } = useTranslation();
  return (alert: FraudAlertInfo) => ({
    title: t(`alerts.rules.${alert.rule}.title`),
    body: t(`alerts.rules.${alert.rule}.body`, {
      staff: alert.who.staff ?? t('alerts.someone'),
      customer: alert.who.customer ?? t('alerts.aCustomer'),
      program: alert.who.program ?? '',
      ...alert.evidence,
    }),
  });
}
